import React, { useCallback, useEffect, useMemo, useState } from "react";
import SummaryCard from "../components/SummaryCard";
import "../styles/dashboard.css";
import { API_BASE } from "../config";

function Dashboard() {
  const [stats, setStats] = useState({
    todaySales: 0,
    totalSales: 0,
    totalAgents: 0,
    totalProducts: 0,
  });

  const [agentCards, setAgentCards] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);

  const authHeader = useMemo(
    () => ({
      Authorization: localStorage.getItem("token"),
    }),
    []
  );

  const money = (v) => {
    const n = Number(v || 0);
    return `₹${n.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDate = (v) => {
    if (!v) return "Not opened yet";

    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "-";

    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "TODAY":
        return "agent-card pink";
      case "LAST_3_DAYS":
        return "agent-card green";
      case "LAST_7_DAYS":
        return "agent-card yellow";
      case "MORE_THAN_7_DAYS":
        return "agent-card saffron";
      default:
        return "agent-card grey";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "TODAY":
        return "Opened today";
      case "LAST_3_DAYS":
        return "Opened in last 3 days";
      case "LAST_7_DAYS":
        return "Opened in last 7 days";
      case "MORE_THAN_7_DAYS":
        return "Not opened for more than 7 days";
      default:
        return "No opening data";
    }
  };

  const loadSummary = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/reports/summary`, {
        headers: authHeader,
      });

      const data = await res.json();

      setStats({
        todaySales: data?.todaySales || 0,
        totalSales: data?.totalSales || 0,
        totalAgents: data?.totalAgents || 0,
        totalProducts: data?.totalProducts || 0,
      });
    } catch (err) {
      console.error("Failed to load summary stats", err);
    }
  }, [authHeader]);

  const loadAgentCards = useCallback(async () => {
    try {
      setLoadingAgents(true);

      const res = await fetch(`${API_BASE}/reports/agent-summary-cards`, {
        headers: authHeader,
      });

      const data = await res.json();

      if (data?.success && Array.isArray(data.data)) {
        setAgentCards(data.data);
      } else {
        setAgentCards([]);
      }
    } catch (err) {
      console.error("Failed to load agent summary cards", err);
      setAgentCards([]);
    } finally {
      setLoadingAgents(false);
    }
  }, [authHeader]);

  useEffect(() => {
    loadSummary();
    loadAgentCards();
  }, [loadSummary, loadAgentCards]);

  return (
    <>
      <div className="dashboard-summary-sticky">
        <div className="summary-row">
          <SummaryCard title="Today's Sales" value={money(stats.todaySales)} />
          <SummaryCard title="Total Sales" value={money(stats.totalSales)} />
          <SummaryCard title="Agents" value={stats.totalAgents} />
          <SummaryCard title="Products" value={stats.totalProducts} />
        </div>
      </div>

      <div className="agent-section-header">
        <div>
          <h3>Agent Sales Summary</h3>
          <p>
            Agent-wise stock, sales and shop opening status for quick follow-up.
          </p>
        </div>

        <button className="refresh-btn" onClick={loadAgentCards}>
          Refresh
        </button>
      </div>

      {loadingAgents ? (
        <div className="agent-loading">Loading agent summary...</div>
      ) : agentCards.length === 0 ? (
        <div className="agent-empty">No agent summary available.</div>
      ) : (
        <div className="agent-card-grid">
          {agentCards.map((agent) => (
            <div
              key={agent.agent_id}
              className={getStatusClass(agent.shop_opening_status)}
            >
              <div className="agent-card-top">
                <div className="agent-avatar">
  {agent.agent_photo_url ? (
    <img
      src={`${API_BASE.replace(/\/api\/?$/, "")}${agent.agent_photo_url}`}
      alt={agent.agent_name || "Agent"}
      className="agent-avatar-img"
      onError={(e) => {
        e.currentTarget.style.display = "none";
      }}
    />
  ) : (
    (agent.agent_name || "A").charAt(0).toUpperCase()
  )}
</div>

                <div>
                  <h4>{agent.agent_name || "-"}</h4>
                  <div className="agent-phone">
                    {agent.mobile_number || "-"}
                  </div>
                </div>
              </div>

              <div className="agent-card-metrics">
                <div className="agent-metric full">
                  <span>Total Stock Value</span>
                  <strong>{money(agent.total_stock_value)}</strong>
                </div>

                <div className="agent-metric">
                  <span>Total Sales</span>
                  <strong>{money(agent.total_sales)}</strong>
                </div>

                <div className="agent-metric">
                  <span>Today's Sales</span>
                  <strong>{money(agent.today_sales)}</strong>
                </div>
              </div>

              <div className="agent-card-footer">
                <div>
                  <span>Last Shop Opening Date</span>
                  <strong>{formatDate(agent.last_shop_opening_date)}</strong>
                </div>

                <div className="agent-status-pill">
                  {getStatusText(agent.shop_opening_status)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export default Dashboard;